import React from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO, ROUTES, SOCIAL_MEDIA_LINKS } from 'config';
import LazyImage from 'components/ui/LazyImage';

const Footer: React.FC = () => {

  return (
    <footer className="bg-slate-800 text-white pt-12 pb-6 px-6">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Section - Logo and Contact */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <div className="w-40 flex items-center justify-center">
              <LazyImage
                src="/images/logo.png"
                alt="Abbey Dental Supplies"
                className="w-full h-full object-cover"
                sizes="160px"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <a href={SOCIAL_MEDIA_LINKS.FACEBOOK} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                <path d="M24.0007 2.85352H20.0007C18.2325 2.85352 16.5368 3.55589 15.2866 4.80614C14.0364 6.05638 13.334 7.75207 13.334 9.52018V13.5202H9.33398V18.8535H13.334V29.5202H18.6673V18.8535H22.6673L24.0007 13.5202H18.6673V9.52018C18.6673 9.16656 18.8078 8.82742 19.0578 8.57737C19.3079 8.32733 19.647 8.18685 20.0007 8.18685H24.0007V2.85352Z" stroke="#E5E7EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                <path d="M23.3327 8.85352H23.346M9.33268 2.85352H22.666C26.3479 2.85352 29.3327 5.83828 29.3327 9.52018V22.8535C29.3327 26.5354 26.3479 29.5202 22.666 29.5202H9.33268C5.65078 29.5202 2.66602 26.5354 2.66602 22.8535V9.52018C2.66602 5.83828 5.65078 2.85352 9.33268 2.85352ZM21.3326 15.3468C21.4972 16.4565 21.3076 17.5898 20.791 18.5855C20.2743 19.5813 19.4568 20.3887 18.4548 20.8931C17.4528 21.3974 16.3172 21.573 15.2097 21.3948C14.1021 21.2165 13.079 20.6936 12.2857 19.9004C11.4925 19.1072 10.9696 18.084 10.7914 16.9765C10.6132 15.8689 10.7887 14.7334 11.2931 13.7313C11.7974 12.7293 12.6049 11.9118 13.6006 11.3952C14.5963 10.8785 15.7296 10.689 16.8393 10.8535C17.9712 11.0214 19.0191 11.5488 19.8282 12.3579C20.6373 13.167 21.1648 14.2149 21.3326 15.3468Z" stroke="#E5E7EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href={SOCIAL_MEDIA_LINKS.LINKEDIN} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
                <path d="M21.3327 10.8535C23.4544 10.8535 25.4892 11.6964 26.9895 13.1967C28.4898 14.697 29.3327 16.7318 29.3327 18.8535V28.1868H23.9993V18.8535C23.9993 18.1463 23.7184 17.468 23.2183 16.9679C22.7182 16.4678 22.0399 16.1868 21.3327 16.1868C20.6254 16.1868 19.9472 16.4678 19.4471 16.9679C18.947 17.468 18.666 18.1463 18.666 18.8535V28.1868H13.3327V18.8535C13.3327 16.7318 14.1755 14.697 15.6758 13.1967C17.1761 11.6964 19.211 10.8535 21.3327 10.8535Z" stroke="#E5E7EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7.99935 12.1868H2.66602V28.1868H7.99935V12.1868Z" stroke="#E5E7EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.33268 8.18685C6.80544 8.18685 7.99935 6.99294 7.99935 5.52018C7.99935 4.04742 6.80544 2.85352 5.33268 2.85352C3.85992 2.85352 2.66602 4.04742 2.66602 5.52018C2.66602 6.99294 3.85992 8.18685 5.33268 8.18685Z" stroke="#E5E7EB" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>

          <div className="space-y-1 text-gray-300 text-sm">
            <p><a href={`mailto:${COMPANY_INFO.email}`}>{COMPANY_INFO.email}</a></p>
            <p>{COMPANY_INFO.phone}</p>
          </div>
        </div>

        {/* Middle Section - Links */}
        <div className="grid grid-cols-3 gap-8">
          <div className="space-y-4">
            <h3 className="text-gray-400 text-sm">Information</h3>
            <div className="space-y-3">
              <Link to={ROUTES.BLOG} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Blog
              </Link>
              <Link to={ROUTES.RESOURCES} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Resources
              </Link>
              <Link to={ROUTES.ABOUT} className="block text-gray-300 text-sm hover:text-white transition-colors">
                About Us
              </Link>
              <Link to={ROUTES.CONTACT} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Contact us
              </Link>
              <a 
                href="https://www.abbeyclearaligners.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-gray-300 text-sm hover:text-white transition-colors"
              >
                Dentist Portal
              </a>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-gray-400 text-sm">Small print</h3>
            <div className="space-y-3">
              <Link to={ROUTES.PRIVACY_POLICY} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to={ROUTES.COOKIES_POLICY} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Cookies Policy
              </Link>
              <Link to={ROUTES.TERMS_AND_CONDITIONS} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link to={ROUTES.RETURNS_POLICY} className="block text-gray-300 text-sm hover:text-white transition-colors">
                Returns Policy
              </Link>
            </div>
          </div>

          {/* Payment Options */}
          <div className="flex items-start justify-end">
            <img
              src="/images/payment_options.jpg"
              alt="Accepted payment methods"
              className="object-contain"
            />
          </div>
        </div>
      </div>

      {/* Copyright and Company Information */}
      <div className="max-w-6xl mx-auto mt-12 pt-8 border-t border-gray-700">
        <div className="text-center text-gray-400 text-xs space-y-2">
          <p>
            Copyright © {new Date().getFullYear()} Abbey Dental Supplies. All Rights Reserved  |  Abbey Dental Supplies is a company registered in England.
          </p>
          <p>
            Registered Office: Abbey Dental Supplies, 4 Imperial Way, Croydon, CR0 4RR  |  VAT No: 649139608
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 